// Rx currently on hold — waiting on a prescriber callback, insurance, patient
// info, or "other". Each row shows the reason, the optional free-text note, who
// placed the hold, and how long it's been sitting. Tech can resume to queue
// (POST /api/rx/{id}/resume) which moves it back to tech_review.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import type { HoldRow, HoldReason } from "@shared/types";

const STORAGE_STORE_KEY = "smartrx-entry.currentStoreCode";

function getCurrentStoreCode(): string {
  return (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_STORE_KEY)) || "GV";
}

const REASON_LABELS: Record<HoldReason, string> = {
  prescriber_callback: "Prescriber callback",
  insurance:           "Insurance",
  patient_callback:    "Patient callback",
  other:               "Other",
};

const REASON_CLASSES: Record<HoldReason, string> = {
  prescriber_callback: "bg-warn-soft text-warn",
  insurance:           "bg-accent-soft text-accent",
  patient_callback:    "bg-[#ecf6e7] text-[#4d7626]",
  other:               "bg-pill text-ink-soft",
};

export function Holds() {
  const [rows, setRows] = useState<HoldRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const storeCode = getCurrentStoreCode();

  function load() {
    const ctrl = new AbortController();
    setLoading(true);
    apiGet<HoldRow[]>(`/holds?store=${storeCode}`, ctrl.signal)
      .then((data) => { setRows(data); setError(null); })
      .catch((e) => { if (!ctrl.signal.aborted) { setRows([]); setError(String(e)); } })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }

  useEffect(() => load(), [storeCode]);

  async function resume(rxId: number) {
    if (!window.confirm("Resume this Rx back to the review queue?")) return;
    setBusyId(rxId);
    try {
      await apiPost<{ rxId: number; status: string }>(`/rx/${rxId}/resume`, {});
      setRows((prev) => prev.filter((r) => r.rxId !== rxId));
    } catch (e) {
      window.alert(`Resume failed: ${e}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-2xl font-semibold">On hold</h2>
        <span className="text-ink-soft text-sm">{storeCode}</span>
      </div>
      <p className="text-ink-soft mb-6">
        {error
          ? <span className="text-bad">Holds unavailable: {error}</span>
          : `${rows.length} prescription${rows.length === 1 ? "" : "s"} parked — pending callback, insurance, or follow-up.`}
      </p>

      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[64px_1.2fr_1fr_140px_1fr_120px_88px_88px] gap-3 px-4 py-2 text-xs uppercase tracking-wide text-ink-soft border-b border-border bg-pill">
          <span>#</span>
          <span>Patient</span>
          <span>Drug</span>
          <span>Reason</span>
          <span>Note</span>
          <span>On hold</span>
          <span>By</span>
          <span></span>
        </div>
        {loading && rows.length === 0 && (
          <p className="text-ink-soft text-sm py-6 text-center">Loading…</p>
        )}
        {!loading && rows.length === 0 && !error && (
          <p className="text-ink-soft text-sm py-10 text-center">
            Nothing on hold for {storeCode}. Holds placed from the review screen will appear here.
          </p>
        )}
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div
              key={r.rxId}
              className="grid grid-cols-[64px_1.2fr_1fr_140px_1fr_120px_88px_88px] gap-3 px-4 py-2.5 items-center text-sm"
            >
              <span className="text-ink-soft">#{r.rxId}</span>
              <span className="truncate"><strong>{r.patientName ?? "—"}</strong></span>
              <span className="truncate">{r.drug}</span>
              <span>
                <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${REASON_CLASSES[r.holdReason] ?? "bg-pill text-ink-soft"}`}>
                  {REASON_LABELS[r.holdReason] ?? r.holdReason}
                </span>
              </span>
              <span className="truncate text-ink-soft" title={r.holdNote ?? ""}>{r.holdNote ?? "—"}</span>
              <span className="text-ink-soft">{relativeTime(r.placedAt)}</span>
              <span className="truncate text-ink-soft">{r.placedByName ?? "—"}</span>
              <span className="flex gap-1 justify-end">
                <Link
                  to={`/review/${r.rxId}`}
                  className="px-2 py-1 rounded bg-surface border border-border text-xs hover:bg-pill"
                >
                  Open
                </Link>
                <button
                  type="button"
                  disabled={busyId === r.rxId}
                  onClick={() => void resume(r.rxId)}
                  className="px-2 py-1 rounded bg-accent text-white text-xs font-medium hover:brightness-95 disabled:opacity-40"
                >
                  {busyId === r.rxId ? "…" : "Resume"}
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60)   return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60)   return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48)    return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}
